import React, { useState, useEffect, useCallback } from 'react';
import './SearchPage.css';

const SearchPage = ({ user, isAdmin }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New request form state
  const [inputs, setInputs] = useState({
    scanLength: '',
    margin: '',
    incLength: '',
    mlsLength: ''
  });

  const [display, setDisplay] = useState({
    patternLength: 0,
    count: 0,
    patternMsg: '',
    countMsg: ''
  });

  const [status, setStatus] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const url = isAdmin
        ? '/api/cd-requests/list'
        : `/api/cd-requests/my-requests/${encodeURIComponent(user.email)}`;
      const response = await fetch(url);
      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [user.email, isAdmin]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    // Calculate pattern length and count
    const scan = parseFloat(inputs.scanLength) || 0;
    const margin = parseFloat(inputs.margin) || 0;
    const patternLen = scan + (2 * margin);
    
    let pMsg = '';
    if (patternLen > 8388.48) {
      pMsg = 'Maximum value cannot exceed 8388.48.';
    }

    // Calculate count only when user has typed something
    let count = 0;
    let cMsg = '';

    if (inputs.scanLength || inputs.margin) {
      const calculatedCount = Math.round(patternLen / 0.128);
      count = calculatedCount < 1025 ? 1025 : calculatedCount;

      if (calculatedCount < 1025) {
        cMsg = 'Calculated count is below minimum. Defaulting to 1025.';
      } else if (count > 65535) {
        cMsg = 'Maximum value cannot exceed 65535.';
      }
    }

    setDisplay({
      patternLength: inputs.scanLength || inputs.margin ? patternLen.toFixed(2) : '0.00',
      count: count || 0,
      patternMsg: pMsg,
      countMsg: cMsg
    });
  }, [inputs.scanLength, inputs.margin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (parseFloat(inputs.scanLength) < 100) {
      setStatus('Error: Scan Length minimum is 100mm');
      return;
    }
    if (parseFloat(inputs.margin) < 1.3) {
      setStatus('Error: Margin minimum is 1.3mm');
      return;
    }
    if (parseFloat(inputs.incLength) < 1.4) {
      setStatus('Error: INC Track Length minimum is 1.4mm');
      return;
    }
    if (parseFloat(inputs.mlsLength) < 1.5) {
      setStatus('Error: MLS Track Length minimum is 1.5mm');
      return;
    }
    // Count validation is handled by defaulting to 1025 in the calculation
    if (display.count > 65535) {
      setStatus('Error: Count maximum is 65535');
      return;
    }

    if (display.patternMsg) {
      setStatus('Error: Please fix pattern length validation');
      return;
    }

    setStatus('Submitting request...');
    setFormLoading(true);

    try {
      const response = await fetch('/api/cd-requests/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scanLength: parseFloat(inputs.scanLength),
          marginPerSide: parseFloat(inputs.margin),
          inc: parseFloat(inputs.incLength),
          mls: parseFloat(inputs.mlsLength),
          customerName: user.name,
          customerEmail: user.email
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus(`Success! Request #${data.cdRequest} created and awaiting approval.`);
        // Close modal first, then reset form so user doesn't see values flicker to 0
        setTimeout(() => {
          fetchRequests();
          setShowNewRequestModal(false);
          setStatus('');
          setInputs({
            scanLength: '',
            margin: '',
            incLength: '',
            mlsLength: ''
          });
        }, 2000);
      } else {
        setStatus('Error: ' + (data.error || 'Server Error'));
      }
    } catch (err) {
      console.error(err);
      setStatus('Error: ' + err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const downloadCoordinates = async (cdRequest) => {
    try {
      const response = await fetch(
        `/api/coordinates/${cdRequest}/export?userEmail=${encodeURIComponent(user.email)}`
      );
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Error downloading coordinates');
        return;
      }

      const text = await response.text();
      const stripped = text
        .split('\n')
        .filter(line => line.trim() !== '')
        .map(line => {
          const cols = line.split(',');
          cols.splice(1, 1);
          return cols.join(',');
        })
        .join('\n');
      const blob = new Blob([stripped], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CodeDisk_Coordinates_${cdRequest}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading coordinates:', error);
      alert('Error downloading coordinates');
    }
  };

  const downloadDxf = async (cdRequest) => {
    try {
      const response = await fetch(
        `/api/coordinates/${cdRequest}/export-dxf?userEmail=${encodeURIComponent(user.email)}`
      );

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Error downloading DXF file');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CodeDisk_Drawing_${cdRequest}.dxf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading DXF:', error);
      alert('Error downloading DXF file');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'New': 'status-new',
      'Closed': 'status-closed',
      'Rejected': 'status-rejected'
    };
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  const filteredRequests = Array.isArray(requests) ? requests.filter(req =>
    req.cd_request.toString().includes(searchTerm) ||
    req.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (req.customer_email && req.customer_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    req.cd_request_status.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  if (loading) {
    return <div className="loading">Loading your requests...</div>;
  }

  return (
    <div className="search-page">
      <div className="page-header">
        <div className="header-content">
          <h2>{isAdmin ? 'All CodeDisk Requests' : 'My CodeDisk Requests'}</h2>
          <p>{isAdmin ? 'View and manage all customer requests' : 'Search and manage your requests'}</p>
        </div>
        <button 
          className="btn-new-request"
          onClick={() => setShowNewRequestModal(true)}
        >
          + New Request
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder={isAdmin ? 'Search by Request #, Customer, Email, or Status...' : 'Search by Request #, Name, or Status...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="search-info">
          Showing {filteredRequests.length} of {requests.length} requests
        </div>
      </div>

      <div className="requests-table">
        <table>
          <thead>
            <tr>
              <th>Request #</th>
                  {isAdmin && <th>Customer</th>}
                  {isAdmin && <th>Email</th>}
              <th>Scan Length</th>
              <th>Margin</th>
              <th>INC</th>
              <th>MLS</th>
              <th>Count</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 11 : 9} style={{ textAlign: 'center', padding: '40px' }}>
                  {searchTerm ? 'No requests match your search' : 'No requests found. Create your first request!'}
                </td>
              </tr>
            ) : (
              filteredRequests.map(request => (
                <tr key={request.cd_request}>
                  <td><strong>#{request.cd_request}</strong></td>
                  {isAdmin && <td>{request.customer_name}</td>}
                  {isAdmin && <td>{request.customer_email}</td>}
                  <td>{request.scan_length} mm</td>
                  <td>{request.margin_per_side} mm</td>
                  <td>{request.inc} mm</td>
                  <td>{request.mls} mm</td>
                  <td>{request.cd_count}</td>
                  <td>{getStatusBadge(request.cd_request_status)}</td>
                  <td>{new Date(request.creation_date).toLocaleDateString()}</td>
                  <td>
                    {request.cd_request_status === 'Closed' && (
                      <div className="action-buttons">
                        <button
                          onClick={() => downloadCoordinates(request.cd_request)}
                          className="btn-download"
                          title="Download coordinate data as CSV"
                        >
                          CSV
                        </button>
                        <button
                          onClick={() => downloadDxf(request.cd_request)}
                          className="btn-download-dxf"
                          title="Download drawing as DXF"
                        >
                          DXF
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Request Modal */}
      {showNewRequestModal && (
        <div className="modal-overlay" onClick={() => setShowNewRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New CodeDisk Request</h2>
              <button 
                className="modal-close"
                onClick={() => setShowNewRequestModal(false)}
              >
                ×
              </button>
            </div>

            {/* Parameter Illustration Diagram */}
            <div className="parameter-diagram">
              <h3>Parameter Reference</h3>
              <div className="diagram-container">
                <img 
                  src="/CS-1.jpg" 
                  alt="CodeDisk Parameter Reference Diagram" 
                  className="diagram-image"
                />
              </div>
              <p className="diagram-note">
                <strong>IW</strong> = INC Track Width | 
                <strong> MW</strong> = MLS Track Width
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="request-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Scan Length (mm) <span className="hint">(Min 100)</span></label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="scanLength" 
                    value={inputs.scanLength} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label>Margin per side (mm) <span className="hint">(Min 1.3)</span></label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="margin" 
                    value={inputs.margin} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label>INC Track Width, IW (mm) <span className="hint">(Min 1.4)</span></label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="incLength" 
                    value={inputs.incLength} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label>MLS Track Width, MW (mm) <span className="hint">(Min 1.5)</span></label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="mlsLength" 
                    value={inputs.mlsLength} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
              </div>

              <div className="calculated-section">
                <h3>Calculated Values</h3>
                <div className="calc-grid">
                  <div className="display-box">
                    <label>Pattern Length</label>
                    <input 
                      type="text" 
                      value={display.patternLength} 
                      readOnly 
                      className={display.patternMsg ? 'error' : ''}
                    />
                    {display.patternMsg && <div className="error-msg">{display.patternMsg}</div>}
                  </div>

                  <div className="display-box">
                    <label>Count</label>
                    <input 
                      type="text" 
                      value={display.count} 
                      readOnly 
                      className={display.countMsg ? 'error' : ''}
                    />
                    {display.countMsg && <div className="error-msg">{display.countMsg}</div>}
                  </div>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={formLoading}>
                {formLoading ? 'Submitting...' : 'Submit Request'}
              </button>

              {status && (
                <div className={`status-message ${status.includes('Error') ? 'error' : 'success'}`}>
                  {status}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;

